(() => {
	'use strict';

	const isEditMode = () =>
		document.body.classList.contains('pimcore-editmode') ||
		document.body.classList.contains('pimcore_editmode');

	const plural = (n, forms) => {
		const a = Math.abs(n);
		if (a === 1) return forms[0];
		if (a >= 2 && a <= 4) return forms[1];
		return forms[2];
	};

	const template = `
		<div class="calculator__body" :class="{ 'is-loss': profit < 0 }">
			<div class="calculator__inputs">
				<div class="calculator__clients">
					<div class="calculator__clients-head">
						<span class="calculator__clients-label">{{ i18n.clientsLabel }}</span>
						<span class="calculator__clients-meta">{{ cfg.days }} {{ i18n.workingDays }}</span>
					</div>
					<div class="calculator__client-buttons" role="tablist">
						<button v-for="n in cfg.clientOptions" :key="n"
								type="button"
								class="calculator__client-btn"
								:class="{ 'is-active': selected === n }"
								role="tab"
								:aria-selected="selected === n ? 'true' : 'false'"
								@click="selected = n">{{ n }}</button>
					</div>
				</div>

				<div class="calculator__price">
					<label class="calculator__price-label" :for="priceInputId">{{ i18n.priceLabel }}</label>
					<div class="calculator__price-input">
						<input type="number" :id="priceInputId" v-model.number="price" min="0" step="10">
						<span class="calculator__price-suffix">{{ cfg.currency }}</span>
						<div class="calculator__price-steppers">
							<button type="button" class="calculator__price-step calculator__price-step--up" @click="stepPrice(10)" :aria-label="i18n.stepUp"></button>
							<button type="button" class="calculator__price-step calculator__price-step--down" @click="stepPrice(-10)" :aria-label="i18n.stepDown"></button>
						</div>
					</div>
				</div>

				<dl class="calculator__facts">
					<div class="calculator__fact">
						<dt>{{ i18n.devicePrice }}</dt>
						<dd>{{ money(cfg.device) }}</dd>
					</div>
					<div class="calculator__fact calculator__fact--divider">
						<dt>
							{{ i18n.monthlyPayment }}
							<span v-if="cfg.paymentMonths" class="calculator__fact-meta">({{ cfg.paymentMonths }}&nbsp;{{ i18n.monthsShort }})</span>
						</dt>
						<dd>{{ money(cfg.payment) }}</dd>
					</div>
				</dl>
			</div>

			<div class="calculator__outputs">
				<div class="calculator__summary">
					<div class="calculator__revenue">
						<div class="calculator__out-label">{{ i18n.revenue }}</div>
						<div class="calculator__revenue-value">{{ money(revenue) }}</div>
						<div class="calculator__revenue-formula">
							{{ selected }} {{ clientsWord }}
							&times; {{ cfg.days }} {{ i18n.daysWord }}
							= {{ fmtInt(measurements) }} {{ i18n.measurementsShort }}
							&times; {{ money(price) }}
						</div>
					</div>
					<div class="calculator__payback">
						<div class="calculator__out-label">{{ i18n.payback }}</div>
						<div class="calculator__payback-value">
							<span>{{ paybackText }}</span>
							<span class="calculator__payback-unit">{{ i18n.months }}</span>
						</div>
					</div>
				</div>

				<div class="calculator__breakdown">
					<div class="calculator__out-label">{{ i18n.breakdown }}</div>
					<div class="calculator__bar" aria-hidden="true">
						<div class="calculator__bar-payment" :style="{ width: paymentRatio + '%' }"></div>
					</div>
					<div class="calculator__breakdown-cols">
						<div class="calculator__breakdown-col calculator__breakdown-col--payment">
							<div class="calculator__breakdown-label">{{ i18n.payment }}</div>
							<div class="calculator__breakdown-value">{{ money(cfg.payment) }}</div>
						</div>
						<div class="calculator__breakdown-col calculator__breakdown-col--profit">
							<div class="calculator__breakdown-label">{{ i18n.netProfit }}</div>
							<div class="calculator__breakdown-value">{{ money(profit) }}</div>
						</div>
					</div>
				</div>

				<div class="calculator__cta" v-html="cfg.ctaHtml"></div>
			</div>
		</div>
	`;

	let mountCounter = 0;

	const mount = (el) => {
		let cfg;
		try { cfg = JSON.parse(el.dataset.config); } catch (e) { return; }

		const fmtInt     = new Intl.NumberFormat(cfg.locale || 'cs-CZ', { maximumFractionDigits: 0 });
		const fmtDecimal = new Intl.NumberFormat(cfg.locale || 'cs-CZ', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

		Vue.createApp({
			template,
			data() {
				return {
					cfg,
					i18n: cfg.i18n || {},
					selected: cfg.clientOptions[0],
					price: cfg.price,
					priceInputId: `calculator-price-${++mountCounter}`,
				};
			},
			computed: {
				measurements() { return this.selected * this.cfg.days; },
				revenue()      { return this.measurements * (Number(this.price) || 0); },
				profit()       { return this.revenue - this.cfg.payment; },
				paymentRatio() { return this.revenue > 0 ? Math.min(100, (this.cfg.payment / this.revenue) * 100) : 100; },
				paybackText()  { return this.revenue > 0 ? fmtDecimal.format(this.cfg.device / this.revenue) : '—'; },
				clientsWord()  { return plural(this.selected, this.i18n.clientsWords || []); },
			},
			methods: {
				fmtInt: (n) => fmtInt.format(n),
				money(n) { return `${fmtInt.format(n)} ${this.cfg.currency}`; },
				stepPrice(delta) {
					const next = (Number(this.price) || 0) + delta;
					this.price = Math.max(0, next);
				},
			},
		}).mount(el);
	};

	const init = () => {
		if (isEditMode()) return;
		if (typeof Vue === 'undefined') return;
		document.querySelectorAll('[data-calculator-vue]').forEach(mount);
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init, { once: true });
	} else {
		init();
	}
})();
